import { useState } from "react";
import { useNavigate } from "react-router";
import styled from "@emotion/styled";
import { fileModule } from "@/modules/file";
import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";

const CreatePageContainer = styled.div`
  padding: 20px;
  max-width: 600px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: 500;
`;

const FileInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  margin-top: 10px;
`;

export const UploadedFilesCreatePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { fileId } = await fileModule.uploadFile({ file });

      // Navigate to the view page
      navigate(atlasRoutes.uploadedFilesView({ params: { uploadedFileId: fileId } }).path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  return (
    <CreatePageContainer>
      <PageTitle>Create Uploaded File</PageTitle>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="file">Select File</Label>
          <FileInput
            id="file"
            type="file"
            onChange={e => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </FormGroup>

        {file && (
          <FormGroup>
            <Label>File Details</Label>
            <div>Name: {file.name}</div>
            <div>Type: {file.type ?? "Unknown"}</div>
            <div>Size: {file.size.toLocaleString()} bytes</div>
          </FormGroup>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button type="submit" disabled={isLoading || !file}>
            {isLoading ? "Uploading..." : "Upload File"}
          </Button>
          <Button type="button" onClick={() => navigate(atlasRoutes.uploadedFiles({}).path)}>
            Cancel
          </Button>
        </ButtonGroup>
      </Form>
    </CreatePageContainer>
  );
};
